/**
 * THE shared write path (NEW_ORDER_V2_PLAN.md rule 9, §2.7, N12, L33): every revamped write of the
 * working sale ends here, on both platforms, with web v1's body. The kiosk, the revamped cart,
 * channel, customer and discount flows, and New Order v2 price and save through it. v1 keeps its
 * own split, untouched, until its cutover (the user, 2026-10-06: no v1 change without approval),
 * so web's body lives twice until then: here, and in `helpers/saleHelpers/index.desktop.ts`. A
 * change to either is ported to the other in the same change (`docs/revamps/ordering.md`).
 *
 *   clone → keep the bill discounts that survive → re-amount the charges → fill `customerRefId`
 *   → price → sweep the coupons the pricing dropped (released at the partner) → save
 *   → reset the approval state if the discount fell
 *
 * The rules are pure (`./params`, `./price`); this file is the one that reads and writes the store.
 * Built in P0 step 10 (2026-10-06).
 */
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";

import store from "../../redux-store";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
import { addDiscountApprovalData } from "../../redux-store/slices/DiscountSlice";
import { subjectFrom } from "../../capabilities/sources/user";
import { approvalResetAfterRepricing } from "../../discounts/approval";
import { dropDiscount } from "../../discounts/release";
import {
  isApiSale,
  priceSale,
  retainedBillDiscounts,
  unappliedCoupons,
  withCharges,
  withCustomerRefId,
} from "./price";
import { pricingParams } from "./params";

declare function structuredClone<T>(value: T): T;

export interface RepriceOptions {
  /** write the priced sale to the store (default); false prices a candidate without committing it */
  commit?: boolean;
  /**
   * no approval-state write either: with `commit: false`, for asking "what would this price to?"
   * ⚠️ A coupon the pricing drops is still swept and released, as in web v1, whose `dryRun`
   * guards only the approval write (flagged, ledger L50).
   */
  dryRun?: boolean;
}

/** Price `data` and, unless told not to, save it as the working sale. Returns the priced sale. */
export function repriceSale(
  data: any,
  { commit = true, dryRun = false }: RepriceOptions = {},
): any {
  const state: any = store.getState();
  const previous = state.saleDataStore?.saleData || {};
  let sale = structuredClone(data);
  const params = pricingParams(sale, state);

  sale = { ...sale, billDiscountList: retainedBillDiscounts(sale) };
  sale = withCharges(sale, structuredClone(previous), state);
  sale = withCustomerRefId(sale);
  let priced = saleutil.calculateForSale(sale, params);

  if (!isApiSale(priced)) {
    for (const coupon of unappliedCoupons(priced, previous)) priced = dropDiscount(coupon, priced);
  }
  if (commit) store.dispatch(saveSaleData(priced));
  if (!dryRun && Math.abs(priced?.discountTotal || 0) < Math.abs(previous?.discountTotal || 0)) {
    const after: any = store.getState(); // as web: read once the sale is saved
    const reset = approvalResetAfterRepricing({
      sale: priced,
      approvalData: after.discountStore?.discountApproval,
      // the one limit the rule reads, as the capability facts parse it (CAPABILITIES.md §3.2:
      // `subject.limits`), never a raw user-record field (audit check O)
      accessPreferences: {
        discountPercentLimit: subjectFrom(after.businessStore?.userData).limits
          .discountPercentLimit,
      },
      validateManualDiscountLimit: saleutil.validateManualDiscountLimit,
    });
    if (reset) store.dispatch(addDiscountApprovalData(reset as any));
  }
  return priced;
}

export * from "./params";
export * from "./price";
